<template>
  <header class="relative px-6 md:px-10 h-24 bg-white">
    <!-- Botón menú + logo -->
    <div
      class="fixed top-4 left-4 md:left-10 bg-white/80 rounded-lg px-3 py-1.5 flex items-center gap-3 shadow-md backdrop-blur-md z-50"
    >
      <button @click="toggleMenu" ref="menuButton">
        <i class="ri-menu-line text-2xl text-[#F37021]"></i>
      </button>
      <RouterLink to="/" @click="handleLogoClick">
        <img src="@/assets/inbros-logotipo3.png" alt="Logo" class="h-6" />
      </RouterLink>
    </div>

    <!-- Botón buscador + login (desktop) -->
    <div
      class="hidden sm:flex fixed top-5 right-6 bg-white/80 rounded-xl px-4 py-2 gap-2 items-center shadow-md backdrop-blur-md z-50 transition-all group"
    >
      <i class="ri-search-line text-xl text-black"></i>
      <input
        type="text"
        v-model="searchQuery"
        @keydown.enter="onKeyEnter"
        placeholder="Busca aquí"
        class="bg-transparent text-black outline-none w-0 group-hover:w-48 transition-all duration-300 overflow-hidden"
      />
      <RouterLink
        to="/login"
        class="text-black font-semibold ml-2 whitespace-nowrap"
      >
        Iniciar sesión
      </RouterLink>
    </div>

    <!-- Botón Portal cliente (mobile) -->
    <RouterLink
      to="/login"
      class="sm:hidden fixed top-4 right-4 bg-white/80 text-black text-xs font-medium rounded-lg px-3 py-1.5 shadow-md hover:bg-white transition backdrop-blur-md z-50"
    >
      Iniciar sesión
    </RouterLink>

    <!-- Menú principal -->
    <div
      v-if="isMenuOpen"
      id="menu-container"
      class="fixed top-[5rem] left-6 bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.15)] px-4 py-3 z-50 transition-all duration-300 opacity-100 translate-y-0"
    >
      <div class="flex gap-3">
        <template v-for="item in menuItems" :key="item.key">
          <RouterLink
            v-if="item.key === 'acerca'"
            to="/about"
            class="text-sm font-medium text-gray-600 hover:text-[#F37021] transition px-2 py-1 rounded"
          >
            {{ item.label }}
          </RouterLink>
          <div
            v-else
            class="text-sm font-medium cursor-pointer px-2 py-1 rounded transition-colors"
            :class="{
              'bg-[#12259F] text-white': submenu === item.key,
              'text-gray-600 hover:text-[#F37021]': submenu !== item.key,
            }"
            @click="toggleSubmenu(item.key)"
          >
            {{ item.label }}
          </div>
        </template>
      </div>
    </div>

    <!-- Submenú -->
    <div
      v-if="submenu && submenuItems[submenu]"
      id="submenu-container"
      class="fixed top-[9.5rem] left-6 bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.15)] p-3 pr-4 z-50 w-[calc(100vw-2rem)] max-w-[700px] transition-all duration-300 opacity-100"
    >
      <div
        class="grid gap-2"
        :class="
          submenu === 'personas'
            ? 'grid-cols-2 sm:grid-cols-4'
            : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5'
        "
      >
        <RouterLink
          v-for="subitem in submenuItems[submenu]"
          :key="subitem"
          :to="generateRoute(subitem)"
          class="flex flex-col items-center justify-center text-center h-16 w-full text-[11px] text-gray-600 font-medium hover:text-[#F37021] transition-colors"
        >
          <span
            v-if="submenu === 'personas' || submenu === 'empresas'"
            class="material-icons text-[18px] mb-[2px]"
          >
            {{ iconMap[subitem] || "category" }}
          </span>
          <span class="leading-snug break-words">
            {{ subitem }}
          </span>
        </RouterLink>
      </div>
    </div>

    <!-- Sugerencias dinámicas (buscador) -->
    <ul
      v-if="searchQuery && filteredRoutes.length"
      class="fixed top-[5rem] right-6 w-64 bg-white shadow-md rounded-md z-50 max-h-64 overflow-auto"
    >
      <li
        v-for="option in filteredRoutes"
        :key="option.label"
        @click="navigateTo(option.path)"
        class="px-4 py-2 text-sm text-[#101541] hover:bg-gray-100 cursor-pointer"
      >
        {{ option.label }}
      </li>
    </ul>
  </header>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";

const generateRoute = (name) => {
  return (
    "/" +
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "-")
  );
};

const router = useRouter();
const searchQuery = ref("");
const isMenuOpen = ref(false);
const submenu = ref(null);

const menuItems = [
  { key: "personas", label: "Personas" },
  { key: "empresas", label: "Empresas" },
  { key: "acerca", label: "Acerca de INBROS" },
];

const submenuItems = {
  personas: [
    "Gastos médicos",
    "Accidentes",
    "Vida",
    "Auto",
    "Hogar",
    "Viajes",
    "Ahorro",
    "Mascotas",
  ],
  empresas: [
    "Responsabilidad civil",
    "Flotillas",
    "Paquete empresarial",
    "Seguro de vida colectivo",
    "Gastos médicos colectivo",
    "Aviación",
    "Embarcaciones",
    "Transporte",
    "Accidentes colectivo",
    "Construcción",
    "Terrorismo",
    "Ciberseguridad",
    "Fianzas",
    "Especiales",
  ],
};
const iconMap = {
  "Gastos médicos": "medical_services",
  Accidentes: "report_problem",
  Vida: "favorite",
  Auto: "directions_car",
  Hogar: "home",
  Viajes: "flight_takeoff",
  Ahorro: "savings",
  Mascotas: "pets",
  // Empresas
  "Responsabilidad civil": "gavel",
  Flotillas: "commute",
  "Paquete empresarial": "business_center",
  "Seguro de vida colectivo": "groups",
  "Gastos médicos colectivo": "local_hospital",
  Aviación: "flight",
  Embarcaciones: "directions_boat",
  Transporte: "local_shipping",
  "Accidentes colectivo": "warning",
  Construcción: "engineering",
  Terrorismo: "security",
  Ciberseguridad: "lock",
  Fianzas: "verified_user",
  Especiales: "star",
};
import { siteContent } from "@/data/searchData";

const filteredRoutes = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return siteContent.filter(
    (page) =>
      page.label.toLowerCase().includes(query) ||
      page.keywords.some((kw) => kw.toLowerCase().includes(query))
  );
});

const navigateTo = (path) => {
  searchQuery.value = "";
  router.push(path);
};

const onKeyEnter = () => {
  if (searchQuery.value.trim()) {
    router.push({ path: "/busqueda", query: { query: searchQuery.value } });
    searchQuery.value = "";
  }
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  submenu.value = null;
};

const toggleSubmenu = (key) => {
  submenu.value = submenu.value === key ? null : key;
};

const handleLogoClick = () => {
  isMenuOpen.value = false;
  submenu.value = null;
};
import { onMounted, onBeforeUnmount } from "vue";

function closeMenu() {
  isMenuOpen.value = false;
  submenu.value = null;
}

const menuButton = ref(null);

function handleGlobalClick(e) {
  setTimeout(() => {
    const menuEl = document.getElementById("menu-container");
    const submenuEl = document.getElementById("submenu-container");

    const clickedMenu = menuEl?.contains(e.target);
    const clickedSubmenu = submenuEl?.contains(e.target);
    const clickedButton = menuButton.value?.contains(e.target);

    if (!clickedMenu && !clickedSubmenu && !clickedButton) {
      closeMenu();
    }
  }, 0);
}

function handleScroll() {
  closeMenu();
}

onMounted(() => {
  document.addEventListener("click", handleGlobalClick);
  window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleGlobalClick);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
@import "https://cdn.jsdelivr.net/npm/remixicon@3.4.0/fonts/remixicon.css";
</style>
